import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Groves = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [groves, setGroves] = useState([]);
  const [userGrove, setUserGrove] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchLocation, setSearchLocation] = useState('');
  const [newGrove, setNewGrove] = useState({
    name: '',
    description: '',
    location: ''
  });

  useEffect(() => {
    fetchGroves();
    fetchUserGrove();
  }, []);

  const fetchGroves = async () => {
    try {
      const response = await axios.get(`${API}/groves`);
      setGroves(response.data);
    } catch (error) {
      console.error('Error fetching groves:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserGrove = async () => {
    if (user?.grove_id) {
      try {
        const response = await axios.get(`${API}/groves`);
        const userGroveData = response.data.find(grove => grove.id === user.grove_id);
        setUserGrove(userGroveData);
      } catch (error) {
        console.error('Error fetching user grove:', error);
      }
    }
  };

  const handleCreateGrove = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/groves`, newGrove, { headers });
      
      setGroves([response.data, ...groves]);
      setUserGrove(response.data);
      setNewGrove({ name: '', description: '', location: '' });
      setShowCreateModal(false);
    } catch (error) {
      console.error('Error creating grove:', error);
    }
  };

  const handleJoinGrove = async (groveId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/groves/${groveId}/join`, {}, { headers });
      
      // Update local state
      const updatedGrove = groves.find(g => g.id === groveId);
      if (updatedGrove) {
        updatedGrove.member_count += 1;
        setUserGrove(updatedGrove);
        setGroves([...groves]);
      }
    } catch (error) {
      console.error('Error joining grove:', error);
    }
  };

  const filteredGroves = groves.filter(grove => 
    searchLocation === '' || 
    grove.location.toLowerCase().includes(searchLocation.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-300 border-t-emerald-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-emerald-700 font-medium">Growing grove connections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100">
      {/* Hero Section */}
      <div 
        className="relative py-20 px-4"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url('https://images.unsplash.com/photo-1623172959921-630212f71058')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-4xl mx-auto text-center text-white">
          <h1 className="text-5xl font-bold mb-6">🌳 Grove Communities</h1>
          <p className="text-xl mb-8 text-gray-200 max-w-2xl mx-auto">
            Connect with like-minded souls in your region. Create local communities, 
            organize events, and build the new earth together through conscious collaboration.
          </p>
          
          {!userGrove ? (
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-full font-semibold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                Start a Grove 🌱
              </button>
            </div>
          ) : (
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 max-w-md mx-auto">
              <h3 className="text-lg font-semibold mb-2">Your Grove</h3>
              <p className="text-emerald-100 mb-4">{userGrove.name}</p>
              <button
                onClick={() => navigate(`/groves/${userGrove.id}`)}
                className="px-6 py-2 bg-white/30 text-white rounded-lg hover:bg-white/40 transition-colors"
              >
                Visit Grove
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        
        {/* Coming Soon Banner */}
        <div className="bg-gradient-to-r from-emerald-100 to-teal-100 border border-emerald-200 rounded-xl shadow-lg p-8 mb-8 text-center">
          <h2 className="text-3xl font-bold text-emerald-800 mb-4">
            **COMING SOON**
          </h2>
          <p className="text-emerald-700 text-lg">
            Groves functionality will be added when our user base is larger. 
            Stay tuned for community-building features!
          </p>
        </div>
        
        {/* Search and Filter */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="flex-1">
              <input
                type="text"
                value={searchLocation}
                onChange={(e) => setSearchLocation(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Search by location (e.g., California, UK, Berlin)..."
              />
            </div>
            <div className="text-gray-600">
              {filteredGroves.length} groves found
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="text-3xl mb-2">🌳</div>
            <h3 className="text-2xl font-bold text-emerald-600">{groves.length}</h3>
            <p className="text-gray-600">Active Groves</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="text-3xl mb-2">👥</div>
            <h3 className="text-2xl font-bold text-emerald-600">
              {groves.reduce((sum, grove) => sum + grove.member_count, 0)}
            </h3>
            <p className="text-gray-600">Community Members</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="text-3xl mb-2">🌍</div>
            <h3 className="text-2xl font-bold text-emerald-600">
              {new Set(groves.map(g => g.location.split(',')[0].trim())).size}
            </h3>
            <p className="text-gray-600">Regions</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="text-3xl mb-2">✨</div>
            <h3 className="text-2xl font-bold text-emerald-600">24/7</h3>
            <p className="text-gray-600">Community Support</p>
          </div>
        </div>

        {/* Groves Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredGroves.map((grove) => (
            <div key={grove.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">🌳</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{grove.name}</h3>
                      <p className="text-sm text-gray-500">{grove.location}</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400">
                    {grove.member_count} members
                  </span>
                </div>

                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  {grove.description}
                </p>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  {user?.grove_id === grove.id ? (
                    <span className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg text-sm font-medium">
                      Your Grove 🏠
                    </span>
                  ) : user?.grove_id ? (
                    <span className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm">
                      Already in a grove
                    </span>
                  ) : (
                    <button
                      onClick={() => handleJoinGrove(grove.id)}
                      className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:shadow-lg transition-shadow text-sm font-medium"
                    >
                      Join Grove
                    </button>
                  )}
                  
                  <button
                    onClick={() => navigate(`/groves/${grove.id}`)}
                    className="px-4 py-2 border border-emerald-300 text-emerald-600 rounded-lg hover:bg-emerald-50 transition-colors text-sm font-medium"
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredGroves.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🌳</div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">
              {searchLocation ? 'No groves found in this area' : 'No groves yet'}
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {searchLocation 
                ? 'Try searching for a different location or start the first grove in your area.'
                : 'Be a pioneer and start the first grove community!'
              }
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg font-medium hover:shadow-lg transition-shadow"
            >
              Start a Grove ✨
            </button>
          </div>
        )}
      </div>

      {/* Create Grove Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Start Your Grove 🌳</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleCreateGrove}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Grove Name *
                  </label>
                  <input
                    type="text"
                    value={newGrove.name}
                    onChange={(e) => setNewGrove({...newGrove, name: e.target.value})}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="e.g., Pioneertown Consciousness Circle"
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location *
                  </label>
                  <input
                    type="text"
                    value={newGrove.location}
                    onChange={(e) => setNewGrove({...newGrove, location: e.target.value})}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="e.g., Pioneertown, CA, USA"
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={newGrove.description}
                    onChange={(e) => setNewGrove({...newGrove, description: e.target.value})}
                    required
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Describe your grove's mission, vision, and what kind of community you want to create..."
                  />
                </div>

                <div className="bg-emerald-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-emerald-800 mb-2">Grove Guide Responsibilities:</h4>
                  <ul className="text-sm text-emerald-700 space-y-1">
                    <li>• Foster inclusive, conscious community</li>
                    <li>• Organize regular gatherings and events</li>
                    <li>• Facilitate group decisions democratically</li>
                    <li>• Maintain grove guidelines and values</li>
                  </ul>
                </div>

                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg font-medium hover:shadow-lg transition-shadow"
                  >
                    Create Grove 🌱
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Groves;